import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { updateWorkflowCategory } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

const TABS = [
  { value: 'sla', label: 'SLA' },
  { value: 'approvals', label: 'Approval requests' },
];

const APPROVAL_OPTIONS = [
  { key: 'approval_required_pass', label: 'Pass / Approve', hint: 'Passing or approving a step is sent as a request.' },
  { key: 'approval_required_fail', label: 'Fail / Reject', hint: 'Failing or rejecting a step is sent as a request.' },
  { key: 'approval_required_skip', label: 'Skip', hint: 'Skipping a step is sent as a request.' },
];

export default function WorkflowSettingsModal({ category, open, onClose }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('sla');
  const [sla, setSla] = useState('');
  const [approvals, setApprovals] = useState({});

  useEffect(() => {
    if (!open) return;
    setTab('sla');
    setSla(category.sla_days ?? '');
    setApprovals(Object.fromEntries(APPROVAL_OPTIONS.map((o) => [o.key, !!category[o.key]])));
  }, [open, category]);

  const mutation = useMutation({
    mutationFn: (payload) => updateWorkflowCategory(category.id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Workflow settings saved.' });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onClose();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not save settings.' }),
  });

  const slaValue = String(sla).trim();
  const slaNumber = Number(slaValue);
  const slaValid = slaValue === '' || (Number.isInteger(slaNumber) && slaNumber >= 1);

  const save = () => {
    if (tab === 'sla') {
      mutation.mutate({ sla_days: slaValue === '' ? null : slaNumber });
    } else {
      mutation.mutate(approvals);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${category.name} — Settings`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} loading={mutation.isPending} disabled={tab === 'sla' && !slaValid}>
            Save
          </Button>
        </>
      }
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'sla' ? (
        <div className="pt-4">
          <Input
            label="Workflow SLA (days)"
            type="number"
            min={1}
            value={sla}
            onChange={(e) => setSla(e.target.value)}
            error={slaValid ? undefined : 'Enter a whole number of days (1 or more).'}
          />
          <p className="mt-2 text-xs text-gray-500">
            Total days a candidate has to complete this workflow. Leave empty to remove the SLA.
          </p>
        </div>
      ) : (
        <div className="pt-4">
          <p className="mb-3 text-sm text-gray-600">
            When enabled, that action on any step of this workflow (except Registration) is sent as a request. It only takes
            effect once someone with the <span className="font-medium">Step submission approval</span> permission approves it.
          </p>
          <div className="space-y-3">
            {APPROVAL_OPTIONS.map((option) => (
              <label key={option.key} className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!approvals[option.key]}
                  onChange={(e) => setApprovals((prev) => ({ ...prev, [option.key]: e.target.checked }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Require approval for {option.label}</span>
                  <span className="block text-xs text-gray-500">{option.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
